// Template type definitions matching the prototype exactly

import {
  PresetFieldMapping,
  PRESET_FIELD_MAPPINGS,
  TEMPLATE_REQUIRED_FIELDS,
  generateFieldResolutionSpec,
} from './fieldMappings';

export type TemplateType =
  | 'vertical-link-list'
  | 'multi-column-links'
  | 'image-card-grid'
  | 'rich-article-cards'
  | 'image-carousel'
  | 'sidebar-link-box'
  | 'sidebar-card-stack'
  | 'inline-reading-callout'
  | 'contextual-link-box'
  | 'related-searches'
  | 'related-videos-carousel';

export type TemplateCategory = 'link-list' | 'card' | 'carousel' | 'sidebar' | 'inline' | 'search';

export interface FieldOption {
  value: string;
  label: string;
}

export interface TemplateField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'checkbox' | 'color' | 'textarea';
  options?: string[];
  default: string | number | boolean;
  min?: number;
  max?: number;
  hint?: string;
  showWhen?: { field: string; value: string | number | boolean };
}

export interface TemplateDefinition {
  id: TemplateType;
  name: string;
  category: TemplateCategory;
  description: string;
  tags: string[];
  fields: TemplateField[];
  /**
   * List of data field IDs that this template uses.
   * These reference fields defined in PRESET_FIELD_MAPPINGS.
   * When present, the API will resolve these fields from crawler data.
   */
  dataFields?: string[];
}

// All 10 templates from the prototype
export const templates: TemplateDefinition[] = [
  {
    id: 'vertical-link-list',
    name: 'Vertical Link List',
    category: 'link-list',
    description: 'Simple stacked links with optional heading and View More',
    tags: ['Basic', 'SEO', 'Blog'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'Related Articles' },
      { name: 'headingTag', label: 'Heading Tag', type: 'select', options: ['h2', 'h3', 'h4', 'h5'], default: 'h3' },
      {
        name: 'linkStyle',
        label: 'Link Style',
        type: 'select',
        options: ['underline', 'none', 'hover-underline'],
        default: 'hover-underline',
      },
      {
        name: 'listStyle',
        label: 'List Style',
        type: 'select',
        options: ['none', 'bullets', 'numbered'],
        default: 'none',
      },
      { name: 'maxLinks', label: 'Maximum Links', type: 'number', default: 8, min: 3, max: 20 },
      { name: 'showViewMore', label: 'Show View More Link', type: 'checkbox', default: true },
      { name: 'viewMoreText', label: 'View More Text', type: 'text', default: 'View More' },
      {
        name: 'containerPadding',
        label: 'Container Padding',
        type: 'select',
        options: ['none', 'sm', 'md', 'lg'],
        default: 'md',
      },
      {
        name: 'itemSpacing',
        label: 'Item Spacing',
        type: 'select',
        options: ['tight', 'normal', 'relaxed'],
        default: 'normal',
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'multi-column-links',
    name: 'Multi-Column Link Modules',
    category: 'link-list',
    description: 'Side-by-side modules (Related Products, Blogs, Tags)',
    tags: ['Footer', 'Multi-section', 'E-commerce'],
    fields: [
      { name: 'sectionHeading', label: 'Main Section Heading', type: 'text', default: '' },
      { name: 'columns', label: 'Number of Columns', type: 'select', options: ['2', '3', '4', '5'], default: '3' },
      { name: 'maxLinksPerModule', label: 'Max Links Per Column', type: 'number', default: 5, min: 3, max: 10 },
      { name: 'showViewMore', label: 'Show View More Per Column', type: 'checkbox', default: true },
      {
        name: 'columnDivider',
        label: 'Column Divider',
        type: 'select',
        options: ['none', 'line', 'shadow'],
        default: 'none',
      },
      {
        name: 'responsiveBehavior',
        label: 'Mobile Behavior',
        type: 'select',
        options: ['stack', 'scroll', '2-col-then-stack'],
        default: 'stack',
      },
      {
        name: 'containerBackground',
        label: 'Container Background',
        type: 'select',
        options: ['none', 'subtle', 'card'],
        default: 'subtle',
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'image-card-grid',
    name: 'Image Card Grid',
    category: 'card',
    description: 'Responsive grid of cards with images and titles',
    tags: ['Visual', 'Products', 'Articles'],
    dataFields: ['image', 'title', 'description'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'Related Products' },
      { name: 'headingTag', label: 'Heading Tag', type: 'select', options: ['h2', 'h3', 'h4'], default: 'h2' },
      {
        name: 'gridColumns',
        label: 'Grid Columns (Desktop)',
        type: 'select',
        options: ['2', '3', '4', '5', 'auto-fit'],
        default: '3',
      },
      {
        name: 'cardStyle',
        label: 'Card Style',
        type: 'select',
        options: ['flat', 'elevated', 'bordered'],
        default: 'elevated',
      },
      {
        name: 'cardSize',
        label: 'Card Size',
        type: 'select',
        options: ['small', 'medium', 'large'],
        default: 'medium',
      },
      {
        name: 'imageAspectRatio',
        label: 'Image Aspect Ratio',
        type: 'select',
        options: ['1:1', '16:9', '4:3', '3:2'],
        default: '16:9',
      },
      { name: 'showTitle', label: 'Show Title', type: 'checkbox', default: true },
      {
        name: 'titlePosition',
        label: 'Title Position',
        type: 'select',
        options: ['below', 'overlay-bottom', 'overlay-center'],
        default: 'below',
      },
      { name: 'showDescription', label: 'Show Description', type: 'checkbox', default: false },
      {
        name: 'cardBorderRadius',
        label: 'Card Border Radius',
        type: 'select',
        options: ['none', 'sm', 'md', 'lg'],
        default: 'md',
      },
      {
        name: 'hoverEffect',
        label: 'Hover Effect',
        type: 'select',
        options: ['none', 'lift', 'zoom', 'darken'],
        default: 'lift',
      },
      { name: 'maxCards', label: 'Maximum Cards', type: 'number', default: 8, min: 4, max: 16 },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'rich-article-cards',
    name: 'Rich Article Cards',
    category: 'card',
    description: 'Cards with image, category, title, date, reading time',
    tags: ['Blog', 'News', 'Publishing'],
    dataFields: ['image', 'title', 'description', 'category', 'publishDate', 'readingTime', 'author', 'excerpt'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'You Might Also Like' },
      {
        name: 'layout',
        label: 'Layout',
        type: 'select',
        options: ['grid', 'carousel', 'horizontal-scroll'],
        default: 'grid',
      },
      { name: 'gridColumns', label: 'Grid Columns', type: 'select', options: ['2', '3', '4'], default: '3' },
      {
        name: 'cardSize',
        label: 'Card Size',
        type: 'select',
        options: ['small', 'medium', 'large'],
        default: 'medium',
      },
      { name: 'showImage', label: 'Show Image', type: 'checkbox', default: true },
      {
        name: 'imageAspectRatio',
        label: 'Image Aspect Ratio',
        type: 'select',
        options: ['16:9', '4:3', '1:1'],
        default: '16:9',
      },
      { name: 'showCategory', label: 'Show Category Tag', type: 'checkbox', default: true },
      { name: 'showDate', label: 'Show Date', type: 'checkbox', default: true },
      { name: 'showReadingTime', label: 'Show Reading Time', type: 'checkbox', default: true },
      { name: 'showExcerpt', label: 'Show Excerpt', type: 'checkbox', default: false },
      { name: 'maxCards', label: 'Maximum Cards', type: 'number', default: 6, min: 3, max: 12 },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'image-carousel',
    name: 'Image Carousel',
    category: 'carousel',
    description: 'Horizontal scrolling carousel with images and titles',
    tags: ['Visual', 'Products', 'Featured'],
    dataFields: ['image', 'title'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'Featured Products' },
      {
        name: 'visibleSlides',
        label: 'Visible Slides (Desktop)',
        type: 'select',
        options: ['3', '4', '5', '6'],
        default: '4',
      },
      {
        name: 'cardStyle',
        label: 'Card Style',
        type: 'select',
        options: ['image-only', 'image-title', 'image-title-meta'],
        default: 'image-title',
      },
      {
        name: 'cardSize',
        label: 'Card Size',
        type: 'select',
        options: ['small', 'medium', 'large'],
        default: 'medium',
      },
      {
        name: 'imageAspectRatio',
        label: 'Image Aspect Ratio',
        type: 'select',
        options: ['1:1', '16:9', '4:3', 'portrait'],
        default: '1:1',
      },
      { name: 'showArrows', label: 'Show Navigation Arrows', type: 'checkbox', default: true },
      { name: 'showDots', label: 'Show Dot Indicators', type: 'checkbox', default: false },
      { name: 'autoPlay', label: 'Auto-Play', type: 'checkbox', default: false },
      { name: 'gap', label: 'Card Gap', type: 'select', options: ['none', 'sm', 'md', 'lg'], default: 'md' },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'sidebar-link-box',
    name: 'Sidebar Link Box',
    category: 'sidebar',
    description: 'Compact vertical list for sidebar placement',
    tags: ['Trending', 'Popular', 'Right Rail'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'Trending Posts' },
      {
        name: 'headingStyle',
        label: 'Heading Style',
        type: 'select',
        options: ['bold', 'uppercase', 'underlined'],
        default: 'uppercase',
      },
      { name: 'maxLinks', label: 'Maximum Links', type: 'number', default: 5, min: 3, max: 10 },
      {
        name: 'linkStyle',
        label: 'Link Display',
        type: 'select',
        options: ['text-only', 'numbered', 'icon-prefix'],
        default: 'text-only',
      },
      { name: 'showDividers', label: 'Show Dividers', type: 'checkbox', default: true },
      { name: 'expandable', label: 'Expandable (Show More)', type: 'checkbox', default: false },
      { name: 'initialVisible', label: 'Initially Visible', type: 'number', default: 5, min: 3, max: 10 },
      {
        name: 'containerStyle',
        label: 'Container Style',
        type: 'select',
        options: ['flat', 'bordered', 'shadowed'],
        default: 'bordered',
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'sidebar-card-stack',
    name: 'Sidebar Card Stack',
    category: 'sidebar',
    description: 'Stacked cards with images for sidebar',
    tags: ['Relevant', 'Featured', 'Right Rail'],
    dataFields: ['thumbnail', 'title', 'category'],
    fields: [
      { name: 'moduleHeading', label: 'Module Heading', type: 'text', default: 'Relevant Blogs' },
      { name: 'maxCards', label: 'Maximum Cards', type: 'number', default: 4, min: 2, max: 8 },
      { name: 'showImage', label: 'Show Thumbnail', type: 'checkbox', default: true },
      {
        name: 'imageSize',
        label: 'Image Size',
        type: 'select',
        options: ['small', 'medium', 'thumbnail'],
        default: 'small',
      },
      { name: 'imagePosition', label: 'Image Position', type: 'select', options: ['left', 'top'], default: 'left' },
      { name: 'showCategory', label: 'Show Category', type: 'checkbox', default: false },
      {
        name: 'cardBackground',
        label: 'Card Background',
        type: 'select',
        options: ['white', 'gray', 'transparent'],
        default: 'white',
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'inline-reading-callout',
    name: 'Inline Reading Callout',
    category: 'inline',
    description: 'Related Reading or See Also box within content',
    tags: ['In-Content', 'Related', 'See Also'],
    fields: [
      { name: 'calloutHeading', label: 'Callout Heading', type: 'text', default: 'Related Reading' },
      {
        name: 'style',
        label: 'Visual Style',
        type: 'select',
        options: ['bordered', 'background', 'left-border'],
        default: 'left-border',
      },
      { name: 'showIcon', label: 'Show Icon', type: 'checkbox', default: true },
      { name: 'maxLinks', label: 'Maximum Links', type: 'number', default: 3, min: 1, max: 5 },
      {
        name: 'linkDisplay',
        label: 'Link Display',
        type: 'select',
        options: ['list', 'inline-comma', 'inline-bullet'],
        default: 'list',
      },
      {
        name: 'floatPosition',
        label: 'Float Position',
        type: 'select',
        options: ['none', 'left', 'right'],
        default: 'none',
      },
      { name: 'width', label: 'Width', type: 'select', options: ['full', 'half', 'third'], default: 'full' },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'contextual-link-box',
    name: 'Contextual Link Box',
    category: 'inline',
    description: 'Mid-article promotion with optional image',
    tags: ['In-Content', 'Dive Deeper', 'CTA'],
    dataFields: ['image', 'title', 'description', 'author', 'readingTime', 'publishDate'],
    fields: [
      {
        name: 'headingSource',
        label: 'Heading Source',
        type: 'select',
        options: ['anchorText', 'title', 'h1', 'custom'],
        default: 'anchorText',
        hint: 'Where to pull the heading text from. "custom" uses the Heading field below.',
      },
      {
        name: 'heading',
        label: 'Heading (Custom)',
        type: 'text',
        default: 'Dive Deeper',
        hint: 'Only used when Heading Source is set to "custom"',
      },
      { name: 'showIcon', label: 'Show Icon', type: 'checkbox', default: false },
      { name: 'showImage', label: 'Show Image', type: 'checkbox', default: true },
      {
        name: 'imagePosition',
        label: 'Image Position',
        type: 'select',
        options: ['left', 'right', 'top'],
        default: 'left',
      },
      {
        name: 'imageWidth',
        label: 'Image Width',
        type: 'text',
        default: '200px',
        hint: 'Width of the image (e.g., 120px, 200px, 300px)',
      },
      {
        name: 'imageHeight',
        label: 'Image Height',
        type: 'text',
        default: '150px',
        hint: 'Height of the image (e.g., 80px, 150px, auto)',
      },
      { name: 'showDescription', label: 'Show Description', type: 'checkbox', default: true },
      { name: 'showAuthor', label: 'Show Author', type: 'checkbox', default: false },
      { name: 'showReadTime', label: 'Show Read Time', type: 'checkbox', default: false },
      { name: 'showPublishDate', label: 'Show Publish Date', type: 'checkbox', default: false },
      { name: 'ctaStyle', label: 'CTA Style', type: 'select', options: ['link', 'button'], default: 'button' },
      {
        name: 'ctaShape',
        label: 'CTA Shape',
        type: 'select',
        options: ['pill', 'rounded', 'square'],
        default: 'pill',
        hint: 'pill = fully rounded, rounded = slight rounding, square = sharp corners',
      },
      { name: 'ctaColor', label: 'CTA Color', type: 'color', default: '#2db77a' },
      { name: 'ctaText', label: 'CTA Text', type: 'text', default: 'Read More' },
      { name: 'ctaShowIcon', label: 'Show CTA Arrow Icon', type: 'checkbox', default: true },
      {
        name: 'hoverColor',
        label: 'Hover Background Color',
        type: 'color',
        default: '#f0fdf4',
        hint: 'Background color when hovering over a box',
      },
      {
        name: 'ctaPosition',
        label: 'CTA Position',
        type: 'select',
        options: ['left', 'right', 'below'],
        default: 'right',
        hint: 'Position of the CTA button/link within the box',
      },
      {
        name: 'borderStyle',
        label: 'Border Style',
        type: 'select',
        options: ['solid', 'dashed', 'none'],
        default: 'solid',
      },
      {
        name: 'maxBoxes',
        label: 'Max Boxes',
        type: 'number',
        default: 6,
        hint: 'Maximum number of link boxes to display',
      },
      { name: 'expandable', label: 'Show More / Expandable', type: 'checkbox', default: false },
      {
        name: 'initialVisible',
        label: 'Initially Visible',
        type: 'number',
        default: 3,
        hint: 'Number of boxes to show before "Show More"',
      },
      { name: 'showMoreColor', label: 'Show More Color', type: 'color', default: '#008fbf' },
      {
        name: 'viewMoreText',
        label: 'View More Text',
        type: 'text',
        default: 'Show More',
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'related-searches',
    name: 'Related Searches',
    category: 'search',
    description: 'Clickable pill-style links for related search terms',
    tags: ['Search', 'E-commerce', 'Navigation', 'Keywords'],
    fields: [
      { name: 'showHeading', label: 'Show Heading', type: 'checkbox', default: true },
      { name: 'headingText', label: 'Heading Text', type: 'text', default: 'Related Searches' },
      {
        name: 'headingStyle',
        label: 'Heading Style',
        type: 'select',
        options: ['simple', 'with-icon', 'minimal'],
        default: 'with-icon',
      },
      {
        name: 'pillStyle',
        label: 'Pill Style',
        type: 'select',
        options: ['filled', 'outlined', 'ghost'],
        default: 'outlined',
      },
      {
        name: 'pillSize',
        label: 'Pill Size',
        type: 'select',
        options: ['small', 'medium', 'large'],
        default: 'medium',
      },
      { name: 'pillColor', label: 'Pill Color', type: 'color', default: '#2563eb' },
      {
        name: 'textColor',
        label: 'Text Color (Filled)',
        type: 'color',
        default: '#ffffff',
        hint: 'Text color for filled style',
      },
      {
        name: 'hoverStyle',
        label: 'Hover Effect',
        type: 'select',
        options: ['darken', 'lighten', 'invert'],
        default: 'darken',
      },
      {
        name: 'showSearchIcon',
        label: 'Show Search Icon',
        type: 'checkbox',
        default: false,
        hint: 'Add search icon to each pill',
      },
      { name: 'layout', label: 'Layout', type: 'select', options: ['wrap', 'scroll', 'grid'], default: 'wrap' },
      {
        name: 'columns',
        label: 'Grid Columns',
        type: 'number',
        default: 3,
        min: 2,
        max: 6,
        hint: 'Only used with grid layout',
      },
      { name: 'maxPills', label: 'Max Pills', type: 'number', default: 10, min: 3, max: 20 },
      {
        name: 'pillsPerRow',
        label: 'Pills Per Row (Initial)',
        type: 'number',
        default: 5,
        min: 2,
        max: 10,
        hint: 'Pills to show before View More',
      },
      { name: 'expandable', label: 'Expandable (View More)', type: 'checkbox', default: false },
      { name: 'viewMoreText', label: 'View More Text', type: 'text', default: 'Show More' },
      {
        name: 'borderRadius',
        label: 'Border Radius',
        type: 'select',
        options: ['square', 'rounded', 'pill'],
        default: 'pill',
      },
      { name: 'backgroundColor', label: 'Background', type: 'color', default: '#f8fafc' },
      {
        name: 'containerPadding',
        label: 'Container Padding',
        type: 'select',
        options: ['none', 'sm', 'md', 'lg', 'custom'],
        default: 'md',
      },
      {
        name: 'customPadding',
        label: 'Custom Padding',
        type: 'text',
        default: '',
        hint: 'CSS padding value, e.g. 10px 20px',
        showWhen: { field: 'containerPadding', value: 'custom' },
      },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
  {
    id: 'related-videos-carousel',
    name: 'Related Videos Carousel',
    category: 'carousel',
    description: 'Horizontal scrolling carousel for related video content with play overlays',
    tags: ['Video', 'Carousel', 'Media', 'YouTube', 'Entertainment'],
    dataFields: ['videoThumbnail', 'title', 'videoDuration', 'channelName', 'viewCount'],
    fields: [
      { name: 'showHeading', label: 'Show Heading', type: 'checkbox', default: true },
      { name: 'headingText', label: 'Heading Text', type: 'text', default: 'Related Videos' },
      {
        name: 'headingStyle',
        label: 'Heading Style',
        type: 'select',
        options: ['simple', 'with-icon', 'minimal'],
        default: 'with-icon',
      },
      {
        name: 'cardStyle',
        label: 'Card Style',
        type: 'select',
        options: ['elevated', 'flat', 'bordered'],
        default: 'elevated',
      },
      {
        name: 'cardSize',
        label: 'Card Size',
        type: 'select',
        options: ['small', 'medium', 'large'],
        default: 'medium',
      },
      { name: 'visibleCards', label: 'Visible Cards', type: 'number', default: 4, min: 2, max: 6 },
      { name: 'maxCards', label: 'Max Cards', type: 'number', default: 10, min: 4, max: 20 },
      { name: 'showPlayButton', label: 'Show Play Button', type: 'checkbox', default: true },
      {
        name: 'playButtonStyle',
        label: 'Play Button Style',
        type: 'select',
        options: ['filled', 'outline', 'minimal'],
        default: 'filled',
      },
      { name: 'playButtonColor', label: 'Play Button Color', type: 'color', default: '#ff0000' },
      { name: 'showDuration', label: 'Show Duration', type: 'checkbox', default: true },
      { name: 'showTitle', label: 'Show Title', type: 'checkbox', default: true },
      { name: 'titleLines', label: 'Title Lines', type: 'select', options: ['1', '2', '3'], default: '2' },
      { name: 'showChannel', label: 'Show Channel Name', type: 'checkbox', default: true },
      { name: 'showViews', label: 'Show View Count', type: 'checkbox', default: true },
      { name: 'showArrows', label: 'Show Navigation Arrows', type: 'checkbox', default: true },
      { name: 'showDots', label: 'Show Dot Indicators', type: 'checkbox', default: false },
      { name: 'autoPlay', label: 'Auto-Scroll', type: 'checkbox', default: false },
      {
        name: 'gap',
        label: 'Card Gap',
        type: 'select',
        options: ['none', 'small', 'medium', 'large'],
        default: 'medium',
      },
      {
        name: 'borderRadius',
        label: 'Border Radius',
        type: 'select',
        options: ['none', 'small', 'medium', 'large'],
        default: 'medium',
      },
      {
        name: 'hoverEffect',
        label: 'Hover Effect',
        type: 'select',
        options: ['none', 'lift', 'scale', 'brighten'],
        default: 'lift',
      },
      { name: 'backgroundColor', label: 'Background Color', type: 'color', default: '#ffffff' },
      { name: 'customCssClass', label: 'Custom CSS Class', type: 'text', default: '' },
      {
        name: 'customCss',
        label: 'Custom CSS',
        type: 'textarea',
        default: '',
        hint: 'Custom CSS rules to override template styles',
      },
    ],
  },
];

// Template categories for filtering
export const TEMPLATE_CATEGORIES: { id: TemplateCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All Templates' },
  { id: 'link-list', label: 'Link Lists' },
  { id: 'card', label: 'Card Layouts' },
  { id: 'carousel', label: 'Carousels' },
  { id: 'sidebar', label: 'Sidebar/Rail' },
  { id: 'inline', label: 'Inline/In-Content' },
  { id: 'search', label: 'Search/Navigation' },
];

// Helper to get template by ID
export function getTemplateById(templateId: TemplateType): TemplateDefinition | undefined {
  return templates.find((t) => t.id === templateId);
}

// Helper to get templates by category
export function getTemplatesByCategory(category: TemplateCategory | 'all'): TemplateDefinition[] {
  if (category === 'all') {
    return templates;
  }
  return templates.filter((t) => t.category === category);
}

// Helper to get default config for a template (includes placement fields)
export function getDefaultConfig(templateId: TemplateType): Record<string, string | number | boolean> {
  const template = getTemplateById(templateId);
  if (!template) return {};

  const config: Record<string, string | number | boolean> = {};

  // Add template-specific fields
  template.fields.forEach((field) => {
    config[field.name] = field.default;
  });

  // Add placement fields
  PLACEMENT_FIELDS.forEach((field) => {
    config[field.name] = field.default;
  });

  return config;
}

// Helper to get all fields for a template (including placement fields)
export function getAllFieldsForTemplate(templateId: TemplateType): TemplateField[] {
  const template = getTemplateById(templateId);
  if (!template) return PLACEMENT_FIELDS;

  return [...template.fields, ...PLACEMENT_FIELDS];
}

// Generated JSON structure (used internally when saving)
export interface GeneratedTemplateJSON {
  templateId: TemplateType;
  templateName: string;
  version: string;
  generatedAt: string;
  configuration: Record<string, string | number | boolean>;
  htmlTemplate: string | null;
}

// Config type for template configuration values
// Note: _generatedJSON is handled separately via type assertion in save handlers
export type TemplateConfig = {
  [key: string]: string | number | boolean | GeneratedTemplateJSON | null | undefined;
};

// Placement fields that are common to all templates
export const PLACEMENT_FIELDS: TemplateField[] = [
  {
    name: 'placementSelector',
    label: 'Placement Selector',
    type: 'text',
    default: '',
    hint: 'CSS selector for where to insert the module (e.g., #main-content, .article-body). Use the bookmarklet to discover and copy a selector.',
  },
  {
    name: 'placementPosition',
    label: 'Placement Position',
    type: 'select',
    options: ['after', 'before', 'inside_start', 'inside_end'],
    default: 'after',
    hint: 'Insert the module before, after, or inline (start/end) within the selected element',
  },
];

// Placement selector for storing where the module should be inserted
export interface PlacementSelector {
  selector: string; // CSS selector or element ID
  position: 'before' | 'after' | 'inside' | 'inside_start' | 'inside_end'; // Where relative to the element
  description?: string; // Human-readable description
  savedAt?: number; // Timestamp when saved
}

/**
 * Template_Settings structure for persisting to database.
 * This is stored as a nested object within 'Overall Metrics' to keep
 * template-related fields grouped together and separate from other module config.
 *
 * Stored in: MODULE_DETAILS['Overall Metrics']['Template_Settings']
 * or: MODULE_DETAILS_DRAFT['Overall Metrics']['Template_Settings']
 */
/**
 * Field resolution configuration for API to resolve data fields at save time.
 */
export interface FieldResolutionConfig {
  templateVariable: string;
  chain: Array<{ source: string; field: string }>;
  fallbackValue: string | null;
  transform?: {
    type: string;
    options?: Record<string, unknown>;
  };
}

export interface TemplateSettingsForDB {
  Template_Id: TemplateType;
  Template_Name: string;
  Template_Version: string;
  Template_Config: TemplateConfig;
  Template_HTML: string;
  Placement: {
    Selector: string;
    Position: 'before' | 'after' | 'inside_start' | 'inside_end';
  };
  /**
   * Field resolution specification for the API.
   * Tells the backend which data fields to resolve and how.
   */
  Field_Mappings?: {
    fields: FieldResolutionConfig[];
  };
  Updated_At: string; // ISO timestamp
}

/**
 * Helper to build Template_Settings object for database persistence.
 * Returns undefined if no template is selected (safe for spreading).
 *
 * @param templateId - The selected template ID
 * @param config - The template config (may include _generatedJSON from TemplateModal)
 * @param htmlTemplateOverride - Optional HTML override (if not provided, uses _generatedJSON.htmlTemplate)
 *
 * Note: The config may contain a `_generatedJSON` field from TemplateModal.handleSave()
 * which has the pre-built structure: { templateId, templateName, version, configuration, htmlTemplate }
 */
export function buildTemplateSettingsForDB(
  templateId: TemplateType | null,
  config: TemplateConfig,
  htmlTemplateOverride?: string
): { Template_Settings: TemplateSettingsForDB } | undefined {
  if (!templateId) return undefined;

  const template = getTemplateById(templateId);
  if (!template) return undefined;

  // Extract _generatedJSON if present (from TemplateModal save)
  const generatedJSON = (config as Record<string, unknown>)._generatedJSON as
    | {
        templateId?: string;
        templateName?: string;
        version?: string;
        configuration?: TemplateConfig;
        htmlTemplate?: string;
      }
    | undefined;

  // Build clean config without internal fields
  const cleanConfig: TemplateConfig = {};
  for (const [key, value] of Object.entries(config)) {
    // Skip internal fields that start with underscore
    if (!key.startsWith('_')) {
      cleanConfig[key] = value as string | number | boolean;
    }
  }

  // Determine HTML template - prefer override, then _generatedJSON, then empty
  const htmlTemplate = htmlTemplateOverride || generatedJSON?.htmlTemplate || '';

  // Generate field resolution specification based on template and config
  const fieldMappings = generateFieldResolutionSpec(templateId, cleanConfig);

  return {
    Template_Settings: {
      Template_Id: templateId,
      Template_Name: generatedJSON?.templateName || template.name,
      Template_Version: generatedJSON?.version || '1.0',
      Template_Config: cleanConfig,
      Template_HTML: htmlTemplate,
      Placement: {
        Selector: (config.placementSelector as string) || '',
        Position: (config.placementPosition as 'before' | 'after' | 'inside_start' | 'inside_end') || 'after',
      },
      Field_Mappings: fieldMappings.fields.length > 0 ? fieldMappings : undefined,
      Updated_At: new Date().toISOString(),
    },
  };
}

/**
 * Helper to extract template state from database Template_Settings.
 * Safely handles modules without template configuration.
 */
export function extractTemplateFromDB(
  templateSettings: TemplateSettingsForDB | undefined | null
): { templateId: TemplateType; config: TemplateConfig } | null {
  if (!templateSettings?.Template_Id) return null;

  return {
    templateId: templateSettings.Template_Id,
    config: {
      ...templateSettings.Template_Config,
      placementSelector: templateSettings.Placement?.Selector || '',
      placementPosition: templateSettings.Placement?.Position || 'after',
    },
  };
}

// Preview link type for row-level preview
export interface PreviewLinkData {
  url: string;
  anchorText: string;
  title?: string;
  description?: string;
  image?: string;
  category?: string;
  readingTime?: string;
  publishDate?: string;
  author?: string;
  excerpt?: string;
  videoThumbnail?: string;
  videoDuration?: string;
  channelName?: string;
  viewCount?: string;
  updatedDate?: string;
}

// Link data type used by template React components
export interface LinkData {
  title: string;
  url: string;
  description?: string;
  image?: string;
  anchorText?: string;
  category?: string;
  date?: string;
  readingTime?: string;
  publishDate?: string;
  author?: string;
  excerpt?: string;
  videoThumbnail?: string;
  videoDuration?: string;
  channelName?: string;
  viewCount?: string;
  updatedDate?: string;
}

// State types
export interface TemplateState {
  selectedTemplate: TemplateType | null;
  config: TemplateConfig;
  isModalOpen: boolean;
  previewDevice: 'desktop' | 'mobile';
  currentStep: 1 | 2 | 3;
  activeCategory: TemplateCategory | 'all';
  placementSelector: PlacementSelector | null;
}
